import classNames from 'classnames';

import React, { forwardRef, HTMLAttributes, MouseEvent, useMemo, useState } from 'react';

import { Image } from '../image';
import { COLLECTION_PLACEHOLDER } from '../lib/collection-placeholder';
import { Typography } from '../typography';

import styles from './collection-item.scss';
import type { Banner } from './types';

const COMPONENT_ID = 'modulor-collection-item';

type CollectionItemProps = Omit<HTMLAttributes<HTMLDivElement>, 'onClick'> &
  Banner & {
    localPlaceholder?: boolean;
    height?: number;
    dataTestId?: string;
  };

export const CollectionItem = forwardRef<HTMLAnchorElement, CollectionItemProps>(
  (
    {
      image,
      title,
      description,
      hero,
      height,
      imgName,
      url,
      style,
      badge,
      localPlaceholder = false,
      onClick,
      className,
      dataTestId = COMPONENT_ID,
    },
    ref,
  ) => {
    const [imageLoaded, setImageLoaded] = useState(false);

    const classes = classNames(className, styles.item, {
      [styles.hero]: hero,
    });

    const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
      if (onClick) {
        e.preventDefault();
        onClick(e, {
          url,
          title,
          image,
        });
      }
    };

    const showImage = useMemo(
      () => (!localPlaceholder ? true : imageLoaded),
      [imageLoaded, localPlaceholder],
    );

    return (
      <a
        ref={ref}
        href={url}
        className={classes}
        style={style}
        onClick={handleClick}
        data-testid={dataTestId}>
        <Typography tag="p" variant="body-semibold">
          {title}
        </Typography>
        <Typography tag="p" variant="caption-1-regular">
          {description}
        </Typography>
        {!showImage ? (
          <div
            className={styles.placeholder}
            dangerouslySetInnerHTML={{
              __html: COLLECTION_PLACEHOLDER,
            }}
          />
        ) : null}
        <Image
          name={imgName}
          src={!imgName && image}
          alt={title}
          height={height + 'px'}
          className={styles.image}
          style={{ display: showImage ? 'unset' : 'none' }}
          onLoad={() => setImageLoaded(true)}
        />
        {badge && badge}
      </a>
    );
  },
);

CollectionItem.displayName = 'CollectionItem';
